import { useState, useEffect, useCallback } from "react";
import { api } from "../api";
import {
  formatDate,
  formatMonth,
  comma,
  pct,
  calcAccuracy,
  accuracyColor,
  accuracyLabel,
  sendTimeConfig,
  diffInfo,
} from "../constants";
import Card from "./ui/Card";
import Btn from "./ui/Btn";
import StatCard from "./ui/StatCard";
import LoadingSpinner from "./ui/LoadingSpinner";
import Select from "./ui/Select";

const TABS = [
  { id: "entries", label: "السجلات اليومية", icon: "📋" },
  { id: "monthly", label: "التقرير الشهري", icon: "📊" },
  { id: "reviewers", label: "أداء المراجعين", icon: "👥" },
  { id: "regions", label: "تحليل المناطق", icon: "🗺️" },
  { id: "sendtime", label: "حالة الإرسال", icon: "⏱️" },
  { id: "diffreasons", label: "أسباب الفروقات", icon: "❗" },
];

const thStyle = {
  padding: "9px 10px",
  color: "#64748b",
  textAlign: "right",
  borderBottom: "1px solid #1e293b",
  whiteSpace: "nowrap",
  fontSize: 11,
  fontWeight: 700,
  position: "sticky",
  top: 0,
  background: "#0f172a",
};

const tdStyle = (opts = {}) => ({
  padding: "8px 10px",
  color: opts.color || "#cbd5e1",
  fontWeight: opts.bold ? 700 : 400,
  fontSize: opts.small ? 11 : 12,
  whiteSpace: opts.nowrap !== false ? "nowrap" : undefined,
  borderBottom: "1px solid #0f172a",
});

const cell = (content, opts = {}) => (
  <td style={tdStyle(opts)} key={opts.key}>
    {content}
  </td>
);

const getLastDay = (m) => {
  const [y, mo] = m.split("-").map(Number);
  return new Date(y, mo, 0).getDate();
};

const EMPTY_LOADING = { entries: false, monthly: false, reviewers: false, regions: false, sendtime: false, diffreasons: false };

export default function Reports({ month, reviewers, stations, toast }) {
  const [activeTab, setActiveTab] = useState("entries");
  const [loadingMap, setLoadingMap] = useState(EMPTY_LOADING);
  const loading = loadingMap[activeTab];
  const setLoadingFor = (tab, v) => setLoadingMap((m) => ({ ...m, [tab]: v }));
  const [entries, setEntries] = useState([]);
  const [entriesMeta, setEntriesMeta] = useState({ total: 0, total_op: 0, total_cam: 0, total_diff_abs: 0 });
  const [monthly, setMonthly] = useState([]);
  const [reviewerPerf, setReviewerPerf] = useState([]);
  const [regionPerf, setRegionPerf] = useState([]);
  const [sendTimeData, setSendTimeData] = useState(null);
  const [diffReasonsData, setDiffReasonsData] = useState(null);

  const [dateFrom, setDateFrom] = useState(month + "-01");
  const [dateTo, setDateTo] = useState(month + "-" + getLastDay(month));
  const [fR, setFR] = useState("");
  const [fS, setFS] = useState("");

  const loadEntries = useCallback(async () => {
    setLoadingFor("entries", true);
    try {
      const p = new URLSearchParams({ date_from: dateFrom, date_to: dateTo, per_page: 500 });
      if (fR) p.set("reviewer_id", fR);
      if (fS) p.set("station_id", fS);
      const res = await api(`/entries?${p}`);
      setEntries(res?.data || res || []);
      setEntriesMeta({
        total: res?.total || 0,
        total_op: res?.total_op || 0,
        total_cam: res?.total_cam || 0,
        total_diff_abs: res?.total_diff_abs || 0,
      });
    } catch {
      setEntries([]);
      setEntriesMeta({ total: 0, total_op: 0, total_cam: 0, total_diff_abs: 0 });
    }
    setLoadingFor("entries", false);
  }, [dateFrom, dateTo, fR, fS]);

  const loadMonthly = useCallback(async () => {
    setLoadingFor("monthly", true);
    try {
      const r = await api(`/reports/monthly-station?month=${month}`);
      setMonthly(r || []);
    } catch { setMonthly([]); }
    setLoadingFor("monthly", false);
  }, [month]);

  const loadReviewerPerf = useCallback(async () => {
    setLoadingFor("reviewers", true);
    try {
      const p = new URLSearchParams({ month });
      if (fR) p.set("reviewer_id", fR);
      const r = await api(`/reports/reviewer-performance?${p}`);
      setReviewerPerf(r || []);
    } catch { setReviewerPerf([]); }
    setLoadingFor("reviewers", false);
  }, [month, fR]);

  const loadRegionPerf = useCallback(async () => {
    setLoadingFor("regions", true);
    try {
      const r = await api(`/reports/region-performance?month=${month}`);
      setRegionPerf(r || []);
    } catch { setRegionPerf([]); }
    setLoadingFor("regions", false);
  }, [month]);

  const loadSendTime = useCallback(async () => {
    setLoadingFor("sendtime", true);
    try {
      const p = new URLSearchParams({ month });
      if (fR) p.set("reviewer_id", fR);
      const r = await api(`/reports/send-time-analysis?${p}`);
      setSendTimeData(r || null);
    } catch { setSendTimeData(null); }
    setLoadingFor("sendtime", false);
  }, [month, fR]);

  const loadDiffReasons = useCallback(async () => {
    setLoadingFor("diffreasons", true);
    try {
      const p = new URLSearchParams({ month });
      if (fR) p.set("reviewer_id", fR);
      const r = await api(`/reports/diff-reasons-by-reviewer?${p}`);
      setDiffReasonsData(r || null);
    } catch { setDiffReasonsData(null); }
    setLoadingFor("diffreasons", false);
  }, [month, fR]);

  useEffect(() => {
    const loaders = {
      entries: loadEntries,
      monthly: loadMonthly,
      reviewers: loadReviewerPerf,
      regions: loadRegionPerf,
      sendtime: loadSendTime,
      diffreasons: loadDiffReasons,
    };
    loaders[activeTab]?.();
  }, [activeTab, loadEntries, loadMonthly, loadReviewerPerf, loadRegionPerf, loadSendTime, loadDiffReasons]);

  useEffect(() => {
    setDateFrom(month + "-01");
    setDateTo(month + "-" + getLastDay(month));
  }, [month]);

  const csvUrl = (type) => {
    const p = new URLSearchParams({ type, month });
    if (fR) p.set("reviewer_id", fR);
    if (dateFrom && type === "entries") p.set("date_from", dateFrom);
    if (dateTo && type === "entries") p.set("date_to", dateTo);
    return `/api/export/csv?${p}`;
  };

  const downloadCsv = async (type) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(csvUrl(type), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        let detail = `خطأ ${res.status}`;
        try {
          const d = await res.json();
          detail = d.detail || d.message || detail;
        } catch {}
        throw new Error(detail);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${type}_report.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast("خطأ في التصدير: " + e.message, "error");
    }
  };

  // Consolidated one-row-per-station Excel statement — separate from the CSV
  // export above because it returns an .xlsx (with formulas/formatting), not
  // a CSV, and because it deliberately merges the per-reviewer split rows
  // that /reports/monthly-station and its CSV keep visible.
  const downloadStationStatement = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`/api/export/station-statement?month=${month}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        let detail = `خطأ ${res.status}`;
        try {
          const d = await res.json();
          detail = d.detail || d.message || detail;
        } catch {}
        throw new Error(detail);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `بيان_المحطات_${month}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast("خطأ في التصدير: " + e.message, "error");
    }
  };

  // Totals come from the backend's own SUM over ALL matching rows, not from
  // reducing the loaded `entries` array — that array is capped at per_page
  // (500), so for any range with more rows than that (e.g. a full month with
  // 1,565 entries), summing it client-side only reflected a slice and showed
  // a much smaller "الفروق" than the real total.
  const totalOp = entriesMeta.total_op;
  const totalCam = entriesMeta.total_cam;
  // "الفروقات" = الفرق الصافي فقط (تشغيلي − كاميرات)، رقم واحد بسيط
  // يتطابق مع طرح رقمي "تشغيلي" و"كاميرات" الظاهرين فوق مباشرة.
  const entriesDiff = diffInfo(totalOp - totalCam);
  const totalDiff = entriesDiff.magnitude;
  const acc = calcAccuracy(totalCam, totalOp);

  const rpTotalOp = reviewerPerf.reduce((s, r) => s + r.total_op, 0);
  const rpTotalCam = reviewerPerf.reduce((s, r) => s + r.total_cam, 0);
  const rpAcc = calcAccuracy(rpTotalCam, rpTotalOp);
  const rpDiff = diffInfo(rpTotalOp - rpTotalCam);

  // Distinct stations covered this month (from the backend, not a per-reviewer
  // sum) — summing each reviewer's stations_covered double-counts any station
  // handed over between two reviewers mid-month.
  const rpTotalStations = reviewerPerf[0]?.month_stations_covered ?? 0;

  return (
    <div>
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginBottom: 20, flexWrap: "wrap", gap: 10,
      }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, margin: 0, color: "#f1f5f9" }}>
          📋 التقارير
        </h2>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ color: "#64748b", fontSize: 13 }}>{formatMonth(month)}</span>
          <Btn onClick={() => downloadCsv(activeTab)} color="#22c55e" size="sm">
            ⬇️ CSV
          </Btn>
          {activeTab === "monthly" && (
            <Btn onClick={downloadStationStatement} color="#14b8a6" size="sm" title="بيان Excel — صف واحد لكل محطة، بصيغة وتنسيق جاهزين للمشاركة">
              📊 بيان المحطات (Excel)
            </Btn>
          )}
          <Btn onClick={() => window.print()} color="#38bdf8" size="sm">
            🖨️ طباعة
          </Btn>
        </div>
      </div>

      <div style={{
        display: "flex", gap: 4, marginBottom: 20, flexWrap: "wrap",
        background: "#0f172a", borderRadius: 12, padding: 4,
      }}>
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            style={{
              flex: 1, minWidth: 100,
              background: activeTab === t.id
                ? "linear-gradient(135deg,#38bdf8,#818cf8)"
                : "transparent",
              color: activeTab === t.id ? "#fff" : "#64748b",
              border: "none", borderRadius: 9,
              padding: "10px 12px", cursor: "pointer",
              fontSize: 12, fontFamily: "inherit",
              fontWeight: activeTab === t.id ? 700 : 400,
              transition: "all .2s",
            }}
          >
            <div style={{ fontSize: 16, marginBottom: 2 }}>{t.icon}</div>
            <div>{t.label}</div>
          </button>
        ))}
      </div>

      {/* ───── Entries Tab ───── */}
      {activeTab === "entries" && (
        <>
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: 12, marginBottom: 16,
          }}>
            <StatCard icon="📊" label="السجلات" value={comma(entriesMeta.total)} color="#38bdf8"
              sub={entries.length < entriesMeta.total ? `المعروض بالجدول: ${comma(entries.length)}` : month} />
            <StatCard icon="⚡" label="تشغيلي" value={comma(totalOp)} color="#a78bfa" />
            <StatCard icon="🎥" label="كاميرات" value={comma(totalCam)} color="#06b6d4" />
            <StatCard icon="↔️" label="الفروقات" value={comma(totalDiff)} color={entriesDiff.color}
              sub={entriesDiff.label} title="الفرق الصافي: تشغيلي − كاميرات" />
            <StatCard icon="🎯" label="الدقة" value={acc !== null ? pct(acc) : "—"} color={acc !== null ? accuracyColor(acc) : "#64748b"}
              sub="الزيادة والنقص = خطأ" title="مقياس مختلف عن «الفروقات»: الدقة تحتسب كل زيادة أو نقص يوميًا كخطأ منفصل، حتى لو تعادلا وألغيا بعضهما في «الفروقات» (الفرق الصافي)." />
          </div>

          <Card style={{ padding: 16 }}>
            <div style={{
              display: "flex", gap: 8, marginBottom: 14,
              flexWrap: "wrap", alignItems: "center",
            }}>
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <label style={{ color: "#64748b", fontSize: 11 }}>من</label>
                <input type="date" value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  style={{
                    background: "#0f172a", border: "1px solid #334155", borderRadius: 8,
                    padding: "6px 9px", color: "#f1f5f9", fontSize: 12, fontFamily: "inherit",
                  }} />
              </div>
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <label style={{ color: "#64748b", fontSize: 11 }}>إلى</label>
                <input type="date" value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  style={{
                    background: "#0f172a", border: "1px solid #334155", borderRadius: 8,
                    padding: "6px 9px", color: "#f1f5f9", fontSize: 12, fontFamily: "inherit",
                  }} />
              </div>
              <div style={{ minWidth: 180 }}>
                <Select
                  value={fR}
                  onChange={setFR}
                  placeholder="كل المراجعين"
                  options={reviewers.map((r) => ({ value: r.id, label: r.name }))}
                />
              </div>
              <div style={{ minWidth: 180 }}>
                <Select
                  value={fS}
                  onChange={setFS}
                  placeholder="كل المحطات"
                  options={stations.map((s) => ({ value: s.id, label: s.name }))}
                />
              </div>
            </div>
            {loading ? <LoadingSpinner /> : (
              <div style={{ overflowX: "auto", maxHeight: 520, borderRadius: 8, border: "1px solid #1e293b" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead><tr style={{ background: "#0f172a" }}>
                    {["التاريخ", "المراجع", "المحطة", "المنطقة", "تشغيلي", "كاميرات", "الفرق", "الدقة", "الإرسال", "السبب", "الملاحظات"].map((h) => (
                      <th key={h} style={thStyle}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {entries.length === 0 ? (
                      <tr><td colSpan={11} style={{ padding: 40, textAlign: "center", color: "#475569" }}>لا توجد سجلات</td></tr>
                    ) : entries.map((e, i) => {
                      const di = diffInfo(e.diff);
                      const sc = sendTimeConfig[e.send_time_status]?.color || "#64748b";
                      const a = calcAccuracy(e.camera_replies, e.operational_replies);
                      return (
                        <tr key={e.id} style={{ background: i % 2 ? "#ffffff03" : "transparent" }}>
                          <td style={tdStyle({ color: "#94a3b8" })}>{formatDate(e.entry_date)}</td>
                          <td style={tdStyle({ color: "#f1f5f9" })}>{e.reviewer_name?.split(" ").slice(0, 2).join(" ")}</td>
                          <td style={tdStyle({ color: "#cbd5e1" })}>{e.station_name}</td>
                          <td style={tdStyle({ color: "#64748b" })}>{e.region}</td>
                          <td style={tdStyle({ color: "#38bdf8", bold: true })}>{comma(e.operational_replies)}</td>
                          <td style={tdStyle({ color: "#a78bfa", bold: true })}>{comma(e.camera_replies)}</td>
                          <td style={tdStyle({ color: di.color, bold: true })}>
                            {comma(di.magnitude)}
                            <div style={{ fontSize: 9, fontWeight: 400 }}>{di.label}</div>
                          </td>
                          <td style={tdStyle({ color: a !== null ? accuracyColor(a) : "#64748b", bold: true })}>{a !== null ? pct(a) : "—"}</td>
                          <td style={tdStyle({ color: sc, small: true })}>
                            {sendTimeConfig[e.send_time_status]?.label || e.send_time_status}
                          </td>
                          <td style={tdStyle({ color: "#f59e0b", small: true })}>{e.diff_reason || "—"}</td>
                          <td style={tdStyle({ color: "#94a3b8", small: true, nowrap: false })}>{e.notes || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      {/* ───── Monthly Tab ───── */}
      {activeTab === "monthly" && (
        <>
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: 12, marginBottom: 16,
          }}>
            {(() => {
              const op = monthly.reduce((s, r) => s + r.total_op, 0);
              const cam = monthly.reduce((s, r) => s + r.total_cam, 0);
              // "الفروقات" = الفرق الصافي فقط (تشغيلي − كاميرات)، يتطابق
              // مع طرح الرقمين الظاهرين فوق مباشرة.
              const di = diffInfo(op - cam);
              const stationsSet = new Set(monthly.map((r) => r.station_name));
              const daysSet = new Set(monthly.map((r) => r.days_count));
              const a = calcAccuracy(cam, op);
              return (
                <>
                  <StatCard icon="🏭" label="محطات" value={stationsSet.size} color="#38bdf8" sub={formatMonth(month)} />
                  <StatCard icon="⚡" label="تشغيلي" value={comma(op)} color="#a78bfa" />
                  <StatCard icon="🎥" label="كاميرات" value={comma(cam)} color="#06b6d4" />
                  <StatCard icon="↔️" label="الفروقات" value={comma(di.magnitude)} color={di.color}
                    sub={di.label} title="الفرق الصافي: تشغيلي − كاميرات" />
                  <StatCard icon="🎯" label="الدقة" value={a !== null ? pct(a) : "—"} color={a !== null ? accuracyColor(a) : "#64748b"}
                    sub="الزيادة والنقص = خطأ" title="مقياس مختلف عن «الفروقات»: الدقة تحتسب كل زيادة أو نقص يوميًا كخطأ منفصل، حتى لو تعادلا وألغيا بعضهما في «الفروقات» (الفرق الصافي)." />
                </>
              );
            })()}
          </div>
          <Card style={{ padding: 16 }}>
            <h3 style={{ color: "#f1f5f9", margin: "0 0 14px", fontSize: 14, fontWeight: 700 }}>
              تقرير {formatMonth(month)} — تفصيل بالمحطة
            </h3>
            {loading ? <LoadingSpinner /> : (
              <div style={{ overflowX: "auto", borderRadius: 8, border: "1px solid #1e293b" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead><tr style={{ background: "#0f172a" }}>
                    {["المحطة", "المنطقة", "المراجع", "تشغيلي", "كاميرات", "الفرق", "الدقة", "أيام"].map((h) => (
                      <th key={h} style={thStyle}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {monthly.length === 0 ? (
                      <tr><td colSpan={8} style={{ padding: 40, textAlign: "center", color: "#475569" }}>لا توجد بيانات</td></tr>
                    ) : monthly.map((r, i) => {
                      const a = r.accuracy != null ? r.accuracy / 100 : null;
                      // الفرق الصافي لهذا الصف فقط = تشغيلي − كاميرات، بيطابق
                      // طرح الرقمين الظاهرين في نفس الصف.
                      const di = diffInfo(r.total_diff);
                      return (
                        <tr key={i} style={{ background: i % 2 ? "#ffffff03" : "transparent" }}>
                          <td style={tdStyle({ color: "#f1f5f9", bold: true })}>{r.station_name}</td>
                          <td style={tdStyle({ color: "#64748b" })}>{r.region}</td>
                          <td style={tdStyle({ color: "#94a3b8" })}>{r.reviewer_name?.split(" ").slice(0, 2).join(" ")}</td>
                          <td style={tdStyle({ color: "#38bdf8", bold: true })}>{comma(r.total_op)}</td>
                          <td style={tdStyle({ color: "#a78bfa", bold: true })}>{comma(r.total_cam)}</td>
                          <td style={tdStyle({ color: di.color, bold: true })}>
                            {comma(di.magnitude)}
                            <div style={{ fontSize: 9, fontWeight: 400 }}>{di.label}</div>
                          </td>
                          <td style={tdStyle({ color: a !== null ? accuracyColor(a) : "#64748b", bold: true })}>{a !== null ? pct(a) : "—"}</td>
                          <td style={tdStyle({ color: "#64748b" })}>{r.days_count}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: "#0f172a", borderTop: "2px solid #1e3a5f" }}>
                      <td colSpan={3} style={{ padding: "9px 10px", color: "#f1f5f9", fontWeight: 800 }}>الإجمالي</td>
                      <td style={{ padding: "9px 10px", color: "#38bdf8", fontWeight: 800 }}>
                        {comma(monthly.reduce((s, r) => s + r.total_op, 0))}
                      </td>
                      <td style={{ padding: "9px 10px", color: "#a78bfa", fontWeight: 800 }}>
                        {comma(monthly.reduce((s, r) => s + r.total_cam, 0))}
                      </td>
                      <td style={{ padding: "9px 10px", color: "#f59e0b", fontWeight: 800 }}>
                        {comma(diffInfo(
                          monthly.reduce((s, r) => s + r.total_op, 0) -
                          monthly.reduce((s, r) => s + r.total_cam, 0)
                        ).magnitude)}
                      </td>
                      <td style={{ padding: "9px 10px", color: "#22c55e", fontWeight: 800 }}>
                        {(() => {
                          const op = monthly.reduce((s, r) => s + r.total_op, 0);
                          const cam = monthly.reduce((s, r) => s + r.total_cam, 0);
                          const a = calcAccuracy(cam, op);
                          return a !== null ? pct(a) : "—";
                        })()}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      {/* ───── Reviewer Performance Tab ───── */}
      {activeTab === "reviewers" && (
        <>
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: 12, marginBottom: 16,
          }}>
            <StatCard icon="👥" label="مراجعون" value={reviewerPerf.length} color="#38bdf8" sub={formatMonth(month)} />
            <StatCard icon="⚡" label="تشغيلي" value={comma(rpTotalOp)} color="#a78bfa" />
            <StatCard icon="🎥" label="كاميرات" value={comma(rpTotalCam)} color="#06b6d4" />
            <StatCard icon="↔️" label="الفروقات" value={comma(rpDiff.magnitude)} color={rpDiff.color}
              sub={rpDiff.label} title="الفرق الصافي: تشغيلي − كاميرات" />
            <StatCard icon="🎯" label="الدقة" value={rpAcc !== null ? pct(rpAcc) : "—"} color={rpAcc !== null ? accuracyColor(rpAcc) : "#64748b"}
              sub="الزيادة والنقص = خطأ" title="مقياس مختلف عن «الفروقات»: الدقة تحتسب كل زيادة أو نقص يوميًا كخطأ منفصل، حتى لو تعادلا وألغيا بعضهما في «الفروقات» (الفرق الصافي)." />
            <StatCard icon="🏭" label="تغطية" value={comma(rpTotalStations)} color="#14b8a6" sub="محطة" />
          </div>

          <Card style={{ padding: 16 }}>
            <div style={{ display: "flex", gap: 8, marginBottom: 14, alignItems: "center" }}>
              <div style={{ minWidth: 180 }}>
                <Select
                  value={fR}
                  onChange={setFR}
                  placeholder="كل المراجعين"
                  options={reviewers.map((r) => ({ value: r.id, label: r.name }))}
                />
              </div>
            </div>
            {loading ? <LoadingSpinner /> : (
              <div style={{ overflowX: "auto", borderRadius: 8, border: "1px solid #1e293b" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead><tr style={{ background: "#0f172a" }}>
                    {["#", "المراجع", "تشغيلي", "كاميرات", "الفرق", "الدقة", "التقييم", "محطات", "أيام", "إرسال قبل"].map((h) => (
                      <th key={h} style={thStyle}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {reviewerPerf.length === 0 ? (
                      <tr><td colSpan={10} style={{ padding: 40, textAlign: "center", color: "#475569" }}>لا توجد بيانات</td></tr>
                    ) : reviewerPerf.map((r, i) => {
                      const c = accuracyColor(r.accuracy);
                      const sendPct = r.send_rate != null ? pct(r.send_rate) : "—";
                      return (
                        <tr key={r.id} style={{ background: i % 2 ? "#ffffff03" : "transparent" }}>
                          <td style={tdStyle({ color: "#475569" })}>{i + 1}</td>
                          <td style={tdStyle({ color: "#f1f5f9", bold: true })}>{r.name}</td>
                          <td style={tdStyle({ color: "#38bdf8", bold: true })}>{comma(r.total_op)}</td>
                          <td style={tdStyle({ color: "#a78bfa", bold: true })}>{comma(r.total_cam)}</td>
                          <td style={tdStyle({ color: diffInfo(r.total_diff).color, bold: true })}>
                            {comma(diffInfo(r.total_diff).magnitude)}
                            <div style={{ fontSize: 9, fontWeight: 400 }}>{diffInfo(r.total_diff).label}</div>
                          </td>
                          <td style={tdStyle({ color: c, bold: true })}>{r.accuracy != null ? pct(r.accuracy) : "—"}</td>
                          <td style={{ padding: "8px 10px" }}>
                            <span style={{
                              background: `${c}22`, color: c, borderRadius: 4, padding: "2px 8px",
                              fontSize: 10, border: `1px solid ${c}44`, fontWeight: 600,
                            }}>{accuracyLabel(r.accuracy)}</span>
                          </td>
                          <td style={tdStyle({ color: "#14b8a6" })}>{r.stations_covered}</td>
                          <td style={tdStyle({ color: "#64748b" })}>{r.entry_count}</td>
                          <td style={tdStyle({ color: r.send_rate >= 0.8 ? "#22c55e" : "#f59e0b", bold: true })}>{sendPct}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      {/* ───── Regions Tab ───── */}
      {activeTab === "regions" && (
        <>
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: 12, marginBottom: 16,
          }}>
            {(() => {
              const op = regionPerf.reduce((s, r) => s + r.total_op, 0);
              const cam = regionPerf.reduce((s, r) => s + r.total_cam, 0);
              const di = diffInfo(op - cam);
              const a = calcAccuracy(cam, op);
              const regionsCount = regionPerf.length;
              const stationsCount = regionPerf.reduce((s, r) => s + r.station_count, 0);
              return (
                <>
                  <StatCard icon="🗺️" label="مناطق" value={regionsCount} color="#38bdf8" sub={formatMonth(month)} />
                  <StatCard icon="🏭" label="محطات" value={stationsCount} color="#14b8a6" />
                  <StatCard icon="⚡" label="تشغيلي" value={comma(op)} color="#a78bfa" />
                  <StatCard icon="🎥" label="كاميرات" value={comma(cam)} color="#06b6d4" />
                  <StatCard icon="↔️" label="الفروقات" value={comma(di.magnitude)} color={di.color}
                    sub={di.label} title="الفرق الصافي: تشغيلي − كاميرات" />
                  <StatCard icon="🎯" label="الدقة" value={a !== null ? pct(a) : "—"} color={a !== null ? accuracyColor(a) : "#64748b"}
                    sub="الزيادة والنقص = خطأ" title="مقياس مختلف عن «الفروقات»: الدقة تحتسب كل زيادة أو نقص يوميًا كخطأ منفصل، حتى لو تعادلا وألغيا بعضهما في «الفروقات» (الفرق الصافي)." />
                </>
              );
            })()}
          </div>
          <Card style={{ padding: 16 }}>
            <h3 style={{ color: "#f1f5f9", margin: "0 0 14px", fontSize: 14, fontWeight: 700 }}>
              تحليل أداء المناطق — {formatMonth(month)}
            </h3>
            {loading ? <LoadingSpinner /> : (
              <div style={{ overflowX: "auto", borderRadius: 8, border: "1px solid #1e293b" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead><tr style={{ background: "#0f172a" }}>
                    {["المنطقة", "محطات", "مراجعون", "تشغيلي", "كاميرات", "الفرق", "الدقة", "أيام", "إرسال قبل"].map((h) => (
                      <th key={h} style={thStyle}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {regionPerf.length === 0 ? (
                      <tr><td colSpan={9} style={{ padding: 40, textAlign: "center", color: "#475569" }}>لا توجد بيانات</td></tr>
                    ) : regionPerf.map((r, i) => {
                      const c = accuracyColor(r.accuracy);
                      const sendPct = r.send_rate != null ? pct(r.send_rate) : "—";
                      return (
                        <tr key={r.region} style={{ background: i % 2 ? "#ffffff03" : "transparent" }}>
                          <td style={tdStyle({ color: "#f1f5f9", bold: true })}>{r.region}</td>
                          <td style={tdStyle({ color: "#14b8a6" })}>{r.station_count}</td>
                          <td style={tdStyle({ color: "#94a3b8" })}>{r.reviewer_count}</td>
                          <td style={tdStyle({ color: "#38bdf8", bold: true })}>{comma(r.total_op)}</td>
                          <td style={tdStyle({ color: "#a78bfa", bold: true })}>{comma(r.total_cam)}</td>
                          <td style={tdStyle({ color: diffInfo(r.total_diff).color, bold: true })}>
                            {comma(diffInfo(r.total_diff).magnitude)}
                            <div style={{ fontSize: 9, fontWeight: 400 }}>{diffInfo(r.total_diff).label}</div>
                          </td>
                          <td style={tdStyle({ color: c, bold: true })}>{r.accuracy != null ? pct(r.accuracy) : "—"}</td>
                          <td style={tdStyle({ color: "#64748b" })}>{r.entry_count}</td>
                          <td style={tdStyle({ color: r.send_rate >= 0.8 ? "#22c55e" : "#f59e0b", bold: true })}>{sendPct}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      {/* ───── Send Time Tab ───── */}
      {activeTab === "sendtime" && (
        <>
          {sendTimeData && (
            <div style={{
              display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
              gap: 12, marginBottom: 16,
            }}>
              {sendTimeData.summary.map((s) => {
                const cfg = sendTimeConfig[s.send_time_status] || { label: s.send_time_status, color: "#64748b" };
                return (
                  <StatCard
                    key={s.send_time_status}
                    icon="⏱️"
                    label={cfg.label}
                    value={comma(s.cnt)}
                    color={cfg.color}
                    sub={`${comma(s.total_op)} تشغيلي`}
                  />
                );
              })}
            </div>
          )}
          <Card style={{ padding: 16 }}>
            <div style={{ display: "flex", gap: 8, marginBottom: 14, alignItems: "center", flexWrap: "wrap" }}>
              <h3 style={{ color: "#f1f5f9", margin: 0, fontSize: 14, fontWeight: 700, flex: 1 }}>
                ⏱️ تحليل حالة الإرسال — {formatMonth(month)}
              </h3>
              <div style={{ minWidth: 180 }}>
                <Select
                  value={fR}
                  onChange={setFR}
                  placeholder="كل المراجعين"
                  options={reviewers.map((r) => ({ value: r.id, label: r.name }))}
                />
              </div>
            </div>
            {loading ? <LoadingSpinner /> : (
              <div style={{ overflowX: "auto", borderRadius: 8, border: "1px solid #1e293b" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead><tr style={{ background: "#0f172a" }}>
                    {["المراجع", "قبل ✅", "بعد ⚠️", "لم يُرسل ❌", "الإجمالي", "نسبة قبل"].map((h) => (
                      <th key={h} style={thStyle}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {(!sendTimeData?.by_reviewer || sendTimeData.by_reviewer.length === 0) ? (
                      <tr><td colSpan={6} style={{ padding: 40, textAlign: "center", color: "#475569" }}>لا توجد بيانات</td></tr>
                    ) : sendTimeData.by_reviewer.map((r, i) => {
                      const total = r.total_entries || 1;
                      const beforeRate = r.before_count / total;
                      return (
                        <tr key={r.id} style={{ background: i % 2 ? "#ffffff03" : "transparent" }}>
                          <td style={tdStyle({ color: "#f1f5f9", bold: true })}>{r.name}</td>
                          <td style={tdStyle({ color: "#22c55e", bold: true })}>{r.before_count}</td>
                          <td style={tdStyle({ color: "#f59e0b", bold: true })}>{r.after_count}</td>
                          <td style={tdStyle({ color: "#ef4444", bold: true })}>{r.not_sent_count}</td>
                          <td style={tdStyle({ color: "#94a3b8" })}>{r.total_entries}</td>
                          <td style={tdStyle({ color: beforeRate >= 0.8 ? "#22c55e" : "#f59e0b", bold: true })}>{pct(beforeRate)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      {/* ───── Diff Reasons Tab ───── */}
      {activeTab === "diffreasons" && (
        <>
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: 12, marginBottom: 16,
          }}>
            <StatCard icon="❗" label="إجمالي الحالات" value={comma(diffReasonsData?.grand_total || 0)} color="#f59e0b" sub={formatMonth(month)} />
            <StatCard icon="🏷️" label="أسباب مختلفة" value={diffReasonsData?.reasons?.length || 0} color="#38bdf8" />
            <StatCard icon="👥" label="مراجعون لديهم فروقات" value={diffReasonsData?.rows?.length || 0} color="#a78bfa" />
            <StatCard
              icon="🔝"
              label="الأكثر تكرارا"
              value={diffReasonsData?.reasons?.[0] || "—"}
              color="#ef4444"
              sub={diffReasonsData?.reasons?.[0] ? `${comma(diffReasonsData.totals[diffReasonsData.reasons[0]])} حالة` : null}
            />
          </div>

          <Card style={{ padding: 16 }}>
            <div style={{ display: "flex", gap: 8, marginBottom: 14, alignItems: "center", flexWrap: "wrap" }}>
              <h3 style={{ color: "#f1f5f9", margin: 0, fontSize: 14, fontWeight: 700, flex: 1 }}>
                ❗ أسباب الفروقات لكل مراجع — {formatMonth(month)}
              </h3>
              <div style={{ minWidth: 180 }}>
                <Select
                  value={fR}
                  onChange={setFR}
                  placeholder="كل المراجعين"
                  options={reviewers.map((r) => ({ value: r.id, label: r.name }))}
                />
              </div>
            </div>
            {loading ? <LoadingSpinner /> : !diffReasonsData || diffReasonsData.reasons.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: "#475569" }}>لا توجد فروقات موثقة لهذا الشهر</div>
            ) : (
              <div style={{ overflowX: "auto", borderRadius: 8, border: "1px solid #1e293b" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead><tr style={{ background: "#0f172a" }}>
                    <th style={{ ...thStyle, position: "sticky", right: 0, zIndex: 1 }}>المراجع</th>
                    {diffReasonsData.reasons.map((rs) => (
                      <th key={rs} style={thStyle}>{rs}</th>
                    ))}
                    <th style={thStyle}>الإجمالي</th>
                  </tr></thead>
                  <tbody>
                    {diffReasonsData.rows.map((row, i) => (
                      <tr key={row.reviewer_id} style={{ background: i % 2 ? "#ffffff03" : "transparent" }}>
                        <td style={{ ...tdStyle({ color: "#f1f5f9", bold: true }), position: "sticky", right: 0, background: i % 2 ? "#0f172aee" : "#020817ee" }}>
                          {row.reviewer_name}
                        </td>
                        {diffReasonsData.reasons.map((rs) => {
                          const v = row.counts[rs] || 0;
                          return (
                            <td key={rs} style={tdStyle({ color: v > 0 ? "#f59e0b" : "#334155", bold: v > 0 })}>
                              {v > 0 ? comma(v) : "—"}
                            </td>
                          );
                        })}
                        <td style={tdStyle({ color: "#ef4444", bold: true })}>{comma(row.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: "#0f172a", borderTop: "2px solid #1e3a5f" }}>
                      <td style={{ padding: "9px 10px", color: "#f1f5f9", fontWeight: 800, position: "sticky", right: 0, background: "#0f172a" }}>
                        الإجمالي
                      </td>
                      {diffReasonsData.reasons.map((rs) => (
                        <td key={rs} style={{ padding: "9px 10px", color: "#f59e0b", fontWeight: 800 }}>
                          {comma(diffReasonsData.totals[rs] || 0)}
                        </td>
                      ))}
                      <td style={{ padding: "9px 10px", color: "#ef4444", fontWeight: 800 }}>
                        {comma(diffReasonsData.grand_total)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </Card>

          <Card style={{ padding: 16, marginTop: 16 }}>
            <h3 style={{ color: "#f1f5f9", margin: "0 0 14px", fontSize: 14, fontWeight: 700 }}>
              🏭 أسباب الفروقات حسب المحطة — {formatMonth(month)}
            </h3>
            {loading ? <LoadingSpinner /> : !diffReasonsData || !diffReasonsData.station_rows?.length ? (
              <div style={{ padding: 40, textAlign: "center", color: "#475569" }}>لا توجد فروقات موثقة لهذا الشهر</div>
            ) : (
              <div style={{ overflowX: "auto", borderRadius: 8, border: "1px solid #1e293b" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead><tr style={{ background: "#0f172a" }}>
                    <th style={{ ...thStyle, position: "sticky", right: 0, zIndex: 1 }}>المحطة</th>
                    <th style={thStyle}>المنطقة</th>
                    <th style={thStyle}>المراجع(ون)</th>
                    {diffReasonsData.reasons.map((rs) => (
                      <th key={rs} style={thStyle}>{rs}</th>
                    ))}
                    <th style={thStyle}>الإجمالي</th>
                  </tr></thead>
                  <tbody>
                    {diffReasonsData.station_rows.map((row, i) => (
                      <tr key={row.station_id} style={{ background: i % 2 ? "#ffffff03" : "transparent" }}>
                        <td style={{ ...tdStyle({ color: "#f1f5f9", bold: true }), position: "sticky", right: 0, background: i % 2 ? "#0f172aee" : "#020817ee" }}>
                          {row.station_name}
                        </td>
                        <td style={tdStyle({ color: "#64748b" })}>{row.region}</td>
                        <td style={tdStyle({ color: "#94a3b8", small: true })}>{row.reviewer_names}</td>
                        {diffReasonsData.reasons.map((rs) => {
                          const v = row.counts[rs] || 0;
                          return (
                            <td key={rs} style={tdStyle({ color: v > 0 ? "#f59e0b" : "#334155", bold: v > 0 })}>
                              {v > 0 ? comma(v) : "—"}
                            </td>
                          );
                        })}
                        <td style={tdStyle({ color: "#ef4444", bold: true })}>{comma(row.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
